import { pick } from '../pick';
import { isOfferRowImprovement } from '../improvement';

export const hasNegotiationOfferId = idOffer => {
  if (idOffer == null) return false;
  const text = String(idOffer).trim();
  return text !== '' && text !== '-';
};

const hasId = value => value != null && String(value).trim() !== '';

/** Counter selection without a saved incoterm-counter id is rejected by BE. */
export const isCounterMissingId = entry =>
  entry?.kind === 'counter' && !hasId(entry?.quantityIncotermCounterId);

/** OfferTable row selectable in negotiation mode (has ID Offer; counter saved). */
export const isNegotiationRowSelectable = row =>
  hasNegotiationOfferId(row?.bidder?.idOffer) &&
  !(
    row?.kind === 'counter' &&
    !hasId(
      row?.detailRow?.quantityIncotermCounterId ??
        pick(
          row?.detailRow,
          'quantityIncotermCounterId',
          'QuantityIncotermCounterId'
        )
    )
  );

/** 1 partner = 1 offer/counter per DU — same key BE dedupes on. */
export const getNegotiationItemKey = entry =>
  [
    entry?.quantityDeliveryTermId ?? '',
    entry?.procurementMitraId ?? '',
    entry?.kind ?? 'offer',
    entry?.quantityIncotermCounterId ?? '',
  ].join('|');

export const dedupeNegotiationItems = (entries = []) => {
  const seen = new Set();
  return (Array.isArray(entries) ? entries : []).filter(entry => {
    const key = getNegotiationItemKey(entry);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

/**
 * Prefill: row keys whose (mitra, kind, counterId) match saved partners.
 * `keyField` follows the table (`rowKey` non-LPG, `selectionKey` LPG).
 */
export const getPreselectedRowKeys = (
  rows = [],
  preselected = [],
  keyField = 'rowKey'
) => {
  if (!preselected?.length) return [];
  const wanted = new Set(
    preselected.map(entry =>
      [
        entry?.procurementMitraId ?? '',
        entry?.kind ?? 'offer',
        entry?.quantityIncotermCounterId ?? '',
      ].join('|')
    )
  );
  return (rows ?? [])
    .filter(row => {
      const counterId =
        row?.kind === 'counter'
          ? (row?.detailRow?.quantityIncotermCounterId ?? '')
          : '';
      return wanted.has(
        [row?.bidder?.id ?? '', row?.kind ?? 'offer', counterId].join('|')
      );
    })
    .map(row => getOfferRowIdentityKey(row, keyField))
    .filter(Boolean);
};

export const getOfferRowIdentityKey = (row, keyField = 'rowKey') => {
  if (keyField === 'selectionKey') {
    return (
      row?.selectionKey ??
      `${row?.bidder?.id ?? row?.bidder?.idOffer ?? 'bidder'}-${row?.kind ?? 'offer'}`
    );
  }
  return row?.rowKey ?? null;
};

export const buildOfferSelectionEntry = ({
  row,
  parcelId,
  requirementIndex,
  requirement,
}) => {
  const bidder = row?.bidder ?? {};
  const detailRow = row?.detailRow ?? {};
  const kind = row?.kind ?? 'offer';
  const quantityIncotermId =
    detailRow.quantityIncotermId ??
    pick(detailRow, 'quantityIncotermId', 'QuantityIncotermId') ??
    requirement?.quantityIncotermId ??
    pick(requirement, 'quantityIncotermId', 'QuantityIncotermId') ??
    null;
  const quantityIncotermCounterId =
    detailRow.quantityIncotermCounterId ??
    pick(detailRow, 'quantityIncotermCounterId', 'QuantityIncotermCounterId') ??
    null;

  return {
    quantityIncotermId,
    quantityIncotermCounterId,
    kind,
    selectionKey: row?.selectionKey ?? row?.rowKey ?? null,
    rowKey: row?.rowKey ?? null,
    procurementMitraId: bidder.id ?? null,
    idOffer: bidder.idOffer ?? null,
    companyName: bidder.companyName ?? null,
    sellerName: bidder.sellerName ?? null,
    quantityDeliveryTermId:
      parcelId ?? detailRow.quantityDeliveryTermId ?? null,
    requirementIndex,
  };
};

/** @deprecated alias — Request Improvement / Negotiation selection entry */
export const buildImprovementSelectionFromOfferRow = buildOfferSelectionEntry;

const collectOfferSelections = ({
  compliantRows = [],
  selectedKeys = [],
  parcelId,
  requirementIndex,
  requirement,
  keyField = 'rowKey',
  excludeImprovement = false,
  includeIdOfferInDedupe = false,
} = {}) => {
  const selectedSet = new Set(selectedKeys);
  const seen = new Set();
  const entries = [];

  compliantRows.forEach(row => {
    const key = getOfferRowIdentityKey(row, keyField);
    if (!key || !selectedSet.has(key)) return;
    if (excludeImprovement && isOfferRowImprovement(row)) return;

    const entry = buildOfferSelectionEntry({
      row,
      parcelId,
      requirementIndex,
      requirement,
    });
    if (includeIdOfferInDedupe && !hasNegotiationOfferId(entry.idOffer)) {
      return;
    }
    // Negotiation: one item per (DU, mitra, kind, counter) — FOB location rows
    // of the same offer collapse into one.
    const dedupeKey = includeIdOfferInDedupe
      ? getNegotiationItemKey(entry)
      : [
          entry.kind ?? 'offer',
          entry.procurementMitraId ?? '',
          entry.quantityIncotermId ?? '',
          entry.quantityIncotermCounterId ?? '',
        ].join('|');
    if (seen.has(dedupeKey)) return;
    seen.add(dedupeKey);
    entries.push(entry);
  });

  return entries;
};

/** EO Offer — Request Improvement (skip rows already flagged improvement). */
export const collectImprovementSelections = args =>
  collectOfferSelections({ ...args, excludeImprovement: true });

/** TE Negotiation tab — Offer selection for handoff draft. */
export const collectNegotiationSelections = args =>
  collectOfferSelections({
    ...args,
    excludeImprovement: false,
    includeIdOfferInDedupe: true,
  });

export const getNegotiationSelectionCoverage = (
  selectionByRequirement = {}
) => {
  const requirementKeys = Object.keys(selectionByRequirement);
  const incompleteRequirementKeys = requirementKeys.filter(
    key => !(selectionByRequirement[key]?.length > 0)
  );
  return {
    requirementKeys,
    incompleteRequirementKeys,
    hasRequirements: requirementKeys.length > 0,
    isComplete: incompleteRequirementKeys.length === 0,
  };
};

export const buildNegotiationHandoffDraft = ({
  procurementId,
  selections = [],
  createdAt = new Date().toISOString(),
} = {}) => ({
  version: 1,
  status: 'draft',
  source: 'tender-evaluation-negotiation',
  procurementId: procurementId ?? null,
  createdAt,
  api: null,
  items: dedupeNegotiationItems(selections).map(entry => ({
    idOffer: entry?.idOffer ?? null,
    procurementMitraId: entry?.procurementMitraId ?? null,
    quantityDeliveryTermId: entry?.quantityDeliveryTermId ?? null,
    quantityIncotermId: entry?.quantityIncotermId ?? null,
    quantityIncotermCounterId: entry?.quantityIncotermCounterId ?? null,
    kind: entry?.kind ?? 'offer',
    requirementIndex: entry?.requirementIndex ?? null,
    selectionKey: entry?.selectionKey ?? entry?.rowKey ?? null,
    companyName: entry?.companyName ?? null,
    sellerName: entry?.sellerName ?? null,
  })),
});

export const NEGOTIATION_HANDOFF_STORAGE_PREFIX = 'te-negotiation-handoff:';

export const getNegotiationHandoffStorageKey = procurementId =>
  `${NEGOTIATION_HANDOFF_STORAGE_PREFIX}${procurementId ?? ''}`;

/** Pure check on flattened delivery detail rows (offer or counter flags). */
export const detailRowsHaveImprovementOffer = (detailRows = []) =>
  (detailRows ?? []).some(
    detailRow =>
      isOfferRowImprovement({ detailRow, kind: 'offer' }) ||
      isOfferRowImprovement({ detailRow, kind: 'counter' })
  );
